import { Box, Button, Flex, HStack, VStack } from '@chakra-ui/react'
import { addDays, format, parse } from 'date-fns'
import React, { useCallback, useMemo, useState } from 'react'
import { REPOSITORY } from '../../../../api/repository/Repository'
import FileDownloader from '../../../../components/FileDownloader/FileDownloader'
import FileUploader from '../../../../components/FileUploader/FileUploader'
import FormButtons from '../../../../components/FormButtons/FormButtons'
import useFormButtons from '../../../../components/FormButtons/hooks/useFormButtons'
import Table from '../../../../components/Table/Table'
import UnderlineHeading from '../../../../components/UnderlineText/UnderlineHeading'
import Warning from '../../../../components/Warning/Warning'
import { useLoadingContext } from '../../../../utils/hooks/Context/LoadingContext'
import { useRequestStatusContext } from '../../../../utils/hooks/Context/RequestStatusContext'
import { useLabels } from '../../../../utils/hooks/useLabels/useLabels'
import { faArrowDown } from '@fortawesome/free-solid-svg-icons'
import { DataLoadError } from '../../../../components/DataLoadError/DataLoadError'
import {
  cleanRut,
  existsInOptions,
  findInOptions,
  normalizeText,
} from '../../../../utils/formats/validators'
const fileKeysToObjectKeys = {
  'Nombre (Obligatorio)': 'sNombre',
  'Apellido Paterno (obligatorio)': 'sApellidoPaterno',
  'Apellido Materno (obligatorio)': 'sApellidoMaterno',
  'RUT (obligatorio)': 'sRut',
  'Fecha Nacimineto (dd-mm-aaaa)': 'dNacimiento',
  'Sexo (M/F)': 'sSexo',
  Dirección: 'sDireccion',
  'Sucursal (obligatorio)': 'sSucursal',
  'Centro Costo': 'sCentroCosto',
  Grupo: 'sGrupo',
}

const columns = [
  {
    Header: 'Centro de costo',
    accessor: 'sCentroCosto',
  },
  {
    Header: 'Sucursal',
    accessor: 'sSucursal',
  },
  {
    Header: 'RUT',
    accessor: 'sRut',
    type: 'dni',
  },
  {
    Header: 'Nombre',
    accessor: 'sNombre',
  },
]

// Descarga: replica la planilla original completa (mismas columnas que el
// template) y agrega el motivo por el que cada fila se separó, para que el
// usuario la corrija y la vuelva a subir tal cual la ingresó.
const reportColumns = [
  ...Object.keys(fileKeysToObjectKeys).map((header) => ({
    Header: header,
    accessor: header,
  })),
  { Header: 'Motivo', accessor: 'Motivo' },
]

// Pantalla: vista acotada del reporte para el modal (los accessors son los
// encabezados originales del Excel, ya que el reporte usa las filas crudas).
const displayReportColumns = [
  { Header: 'RUT', accessor: 'RUT (obligatorio)' },
  { Header: 'Nombre', accessor: 'Nombre (Obligatorio)' },
  { Header: 'Centro Costo', accessor: 'Centro Costo' },
  { Header: 'Grupo', accessor: 'Grupo' },
  { Header: 'Motivo', accessor: 'Motivo' },
]

function excelDateToJSDate(excelDate) {
  const date = new Date(Math.round((excelDate - (25567 + 1)) * 86400 * 1000))
  return date.toISOString().split('T')[0]
}

const objectKeyByFileKey = Object.keys(fileKeysToObjectKeys).reduce(
  (acc, fileKey) => ({
    ...acc,
    [normalizeText(fileKey)]: fileKeysToObjectKeys[fileKey],
  }),
  {},
)

const getObjectKey = (fileKey) => objectKeyByFileKey[normalizeText(fileKey)]

const rawRowToReportRow = (raw) => {
  const valueByNormalizedKey = Object.keys(raw).reduce(
    (acc, key) => ({ ...acc, [normalizeText(key)]: raw[key] }),
    {},
  )
  return Object.keys(fileKeysToObjectKeys).reduce(
    (acc, header) => ({
      ...acc,
      [header]: valueByNormalizedKey[normalizeText(header)] ?? '',
    }),
    {},
  )
}

const fileObjectFormat = (object) => {
  return Object.keys(object).reduce((acc, el) => {
    const key = getObjectKey(el)
    if (!key) {
      return acc
    }
    if (key === 'sRut') {
      return { ...acc, [key]: `${object[el]}` }
    }
    if (key === 'dNacimiento') {
      const formatDate = excelDateToJSDate(object[el])
      const date = format(
        addDays(parse(formatDate, 'yyyy-MM-dd', new Date()), -1),
        'dd-MM-yyyy',
      )
      return {
        ...acc,
        [key]: date,
      }
    }
    if (['sGrupo', 'sSucursal', 'sCentroCosto'].includes(key)) {
      return {
        ...acc,
        [key]: `${object[el] ?? ''}`.trim(),
      }
    }
    return { ...acc, [key]: object[el] }
  }, {})
}

const fileDataFormat = (data) => {
  const { sEmpresa } = REPOSITORY.local.company.getCompanyData()
  return data.map((item) => ({
    ...fileObjectFormat(item),
    sTipoEmpleado: 'PE',
    sEmpresa,
    bAlimentacion: true,
  }))
}
export default function Massive() {
  const { texts } = useLabels()

  const [showDataLoadError, setShowDataLoadError] = useState(false)
  const [dataLoadError, setDataLoadError] = useState()
  // 'error' bloquea (ninguna fila creable); 'partial' es creación parcial (hay
  // filas válidas que se crearán + filas separadas que se devuelven a corregir).
  const [dataLoadErrorMode, setDataLoadErrorMode] = useState('error')

  const { onBack } = useFormButtons()
  const [data, setData] = useState([])
  // Filas crudas de la planilla (columnas originales), para reconstruir el
  // reporte de errores "igual a la que ingresó" + motivo. Correlaciona por
  // índice con `data`.
  const [rawData, setRawData] = useState([])
  // Filas ya validadas (local + backend) y saneadas, listas para crear en el
  // Continuar. Solo se envían estas: las separadas quedan fuera.
  const [rowsToCreate, setRowsToCreate] = useState([])
  const [validated, setValidated] = useState(false)
  const { handleOpen, handleSetTitle, handleChangeBody, handleClose } =
    useRequestStatusContext()
  const { handleOpen: handleOpenLoading, handleClose: handleCLoseLoading } =
    useLoadingContext()

  // Opciones válidas de la empresa para verificar que el centro de costo y el
  // grupo escritos en la planilla existan realmente.
  const companyData = useMemo(
    () => REPOSITORY.local.company.getCompanyData(),
    [],
  )
  const costCenterOptions = useMemo(
    () =>
      (companyData.sCentroCosto || []).filter(
        (option) => option?.label !== 'Todos',
      ),
    [companyData],
  )
  const groupOptions = useMemo(() => companyData.lGrupo || [], [companyData])
  const branchOptions = useMemo(
    () => companyData.sSucursal || [],
    [companyData],
  )

  // Validación por fila. Toda fila con problema se SEPARA (no se crea) y se
  // devuelve al usuario para que la corrija y la vuelva a subir. Motivos:
  //  - Campos obligatorios faltantes (Nombre, Apellidos, RUT, Sucursal).
  //  - Centro de costo escrito pero inexistente en la empresa.
  //  - Grupo escrito pero inexistente en la empresa.
  // El match de centro de costo/grupo solo se evalúa cuando la columna trae
  // dato: columna vacía es válida y cae al valor por defecto (grupo "Libre").
  const getRowErrors = useCallback(
    (row) => {
      const errors = []
      if (
        !row.sRut ||
        !row.sNombre ||
        !row.sSucursal ||
        !row.sApellidoPaterno ||
        !row.sApellidoMaterno
      ) {
        errors.push(
          'Faltan campos obligatorios (Nombre, Apellido paterno, Apellido materno, RUT o Sucursal)',
        )
      }
      if (
        row.sCentroCosto &&
        !existsInOptions(row.sCentroCosto, costCenterOptions)
      ) {
        errors.push(
          `El centro de costo "${row.sCentroCosto}" no existe o está mal escrito. Corrígelo o deja la columna vacía para usar el valor por defecto`,
        )
      }
      if (row.sGrupo && !existsInOptions(row.sGrupo, groupOptions)) {
        errors.push(
          `El grupo "${row.sGrupo}" no existe o está mal escrito. Corrígelo o deja la columna vacía para asignar el grupo "Libre" por defecto`,
        )
      }
      return errors
    },
    [costCenterOptions, groupOptions],
  )

  const sanitizeForBackend = useCallback(
    (rows) =>
      rows.map((element) => {
        const finalElement = { ...element }
        finalElement.sGrupo =
          findInOptions(finalElement.sGrupo, groupOptions)?.label || ''
        finalElement.sCentroCosto =
          findInOptions(finalElement.sCentroCosto, costCenterOptions)?.label ||
          ''
        finalElement.sSucursal =
          findInOptions(finalElement.sSucursal, branchOptions)?.label ||
          finalElement.sSucursal ||
          ''
        return finalElement
      }),
    [groupOptions, costCenterOptions, branchOptions],
  )

  const handleFile = useCallback((fileData) => {
    setValidated(false)
    setRowsToCreate([])
    if (fileData) {
      const formated = fileDataFormat(fileData)
      setData(formated)
      setRawData(fileData)
    } else {
      setData([])
      setRawData([])
    }
  }, [])

  const handleOkStatus = ({ status, title }) => {
    handleCLoseLoading()
    handleSetTitle(title, status)
    handleChangeBody(
      <Button variant="primary" onClick={handleClose}>
        {status === 200 ? 'Continuar' : 'Contactar a soporte ahora'}
      </Button>,
    )
    handleOpen()
  }
  const handleErrorFile = ({ status, error }) => {
    handleCLoseLoading()
    handleSetTitle(error, status)
    handleChangeBody(
      <Button variant="primary" onClick={handleClose}>
        Volver
      </Button>,
    )
    handleOpen()
  }
  const onSubmit = async () => {
    // Solo se crean las filas válidas guardadas en el Verificar. Las separadas
    // (con error) quedaron fuera y el usuario ya las tiene para corregir.
    if (rowsToCreate.length === 0) {
      handleErrorFile({
        status: 400,
        error:
          'No hay registros válidos para crear. Verifica la información primero.',
      })
      return
    }
    handleOpenLoading()
    await REPOSITORY.wsEmpresa.employees
      .createEmployees(rowsToCreate)
      .then((res) => {
        handleOkStatus({ status: res.status, title: 'Bolsillos creados' })
      })
      .catch((error) => {
        console.error('error', error)
        handleErrorFile({
          status: 400,
          error: error.response.data.error,
        })
      })
    handleCLoseLoading()
  }

  const validateData = async () => {
    setValidated(false)
    setRowsToCreate([])
    if (data.length === 0) {
      handleErrorFile({
        status: 400,
        error: 'No se ha cargado archivo o el archivo no tiene datos',
      })
      return
    }

    // 1) Validación local por fila: se separan las filas con problema (campos
    //    obligatorios, o centro de costo / grupo escritos pero inexistentes) de
    //    las válidas. Las separadas NO se crean: se devuelven al usuario tal
    //    cual las ingresó (planilla original + Motivo) para que las corrija y
    //    las vuelva a subir. Las válidas siguen su curso.
    const perRow = data.map((row, index) => ({
      row,
      raw: rawData[index] || {},
      errors: getRowErrors(row),
    }))
    const invalidLocal = perRow.filter((r) => r.errors.length > 0)
    const validLocal = perRow.filter((r) => r.errors.length === 0)
    const localReport = invalidLocal.map((r) => ({
      ...rawRowToReportRow(r.raw),
      Motivo: r.errors.join(' | '),
    }))

    // Ninguna fila válida: no hay nada que crear → bloqueo, corregir y resubir.
    if (validLocal.length === 0) {
      setDataLoadErrorMode('error')
      setDataLoadError(localReport)
      setShowDataLoadError(true)
      return
    }

    // 2) Se validan en el backend SOLO las filas válidas localmente (duplicados,
    //    etc.). Las que el backend rechace se suman al reporte de separadas y se
    //    excluyen de la creación.
    const finalData = sanitizeForBackend(validLocal.map((r) => r.row))
    handleOpenLoading()
    await REPOSITORY.wsEmpresa.employees
      .validateCreateEmployeesMasive(finalData)
      .then((res) => {
        const backendRows = Array.isArray(res.data.lDatosCuenta)
          ? res.data.lDatosCuenta
          : []
        // OJO: lDatosCuenta trae TODAS las filas, también las exitosas (con
        // sComentario "Operación Realizada Con Éxito"). Solo son errores las
        // que traen un comentario que NO indica éxito.
        const backendErrors = backendRows.filter((item) => {
          const comment = normalizeText(item?.sComentario)
          return Boolean(comment) && !comment.includes('exito')
        })
        const reasonByRut = backendErrors.reduce((acc, item) => {
          acc[cleanRut(item.sRUT)] = item.sComentario || 'No se pudo crear'
          return acc
        }, {})
        const errorRuts = new Set(Object.keys(reasonByRut))

        // Filas válidas localmente que el backend rechazó → al reporte.
        const backendReport = validLocal
          .filter((r) => errorRuts.has(cleanRut(r.row.sRut)))
          .map((r) => ({
            ...rawRowToReportRow(r.raw),
            Motivo: reasonByRut[cleanRut(r.row.sRut)],
          }))
        const creatable = validLocal.filter(
          (r) => !errorRuts.has(cleanRut(r.row.sRut)),
        )
        const report = [...localReport, ...backendReport]

        if (creatable.length > 0) {
          setRowsToCreate(sanitizeForBackend(creatable.map((r) => r.row)))
          setValidated(true)
        }

        if (report.length === 0) {
          // Todo válido: sin reporte, se habilita continuar.
          setDataLoadError([])
          setShowDataLoadError(false)
        } else if (creatable.length > 0) {
          // Creación parcial: hay filas que se crearán al continuar y filas
          // separadas que se devuelven para corregir y resubir.
          setDataLoadErrorMode('partial')
          setDataLoadError(report)
          setShowDataLoadError(true)
        } else {
          // Ninguna fila creable tras el backend: bloqueo, corregir y resubir.
          setDataLoadErrorMode('error')
          setDataLoadError(report)
          setShowDataLoadError(true)
        }
      })
      .catch((error) => {
        console.error('error', error)
        handleErrorFile({
          status: 400,
          error: error.response.data.error,
        })
      })
    handleCLoseLoading()
  }

  return (
    <VStack
      spacing="40px"
      mt="60px"
      mb="40px"
      h="90%"
      justifyContent="space-between"
    >
      <Box>
        <Warning warningText={texts.massive_register_advertise} />
        <Box mt="16px">
          <Warning warningText={texts.massive_register_optional_columns} />
        </Box>
        <HStack spacing="82px" mt="45px" alignItems="flex-start">
          <FileDownloader
            linkToFile="/files/Amipass_CargaSocios.xls"
            icon={faArrowDown}
          />
          <Flex direction="column">
            <UnderlineHeading variant="h5" text={texts.upload_excel_template} />
            <Box mt="43px">
              <FileUploader handleFile={handleFile} />
            </Box>
          </Flex>
          <Box>
            <Button
              mt="90px"
              variant="gray"
              disabled={validated}
              onClick={validateData}
            >
              Verificar información
            </Button>
          </Box>
        </HStack>
        <Box mt={10}>
          {data.length > 0 && <Table data={data} columns={columns} />}
        </Box>
      </Box>
      <FormButtons
        onBack={onBack}
        onContinue={onSubmit}
        disabled={!validated}
      />
      <DataLoadError
        data={dataLoadError}
        show={showDataLoadError}
        columns={displayReportColumns}
        downloadColumns={reportColumns}
        mode={dataLoadErrorMode}
        // En modo 'partial' el modal solo informa qué filas se separaron:
        // cerrarlo NO crea. La creación de las filas válidas queda en el botón
        // "Continuar" (onSubmit), que el usuario presiona explícitamente una vez
        // revisado el reporte.
        continueLabel="Entendido"
        onContinue={() => {
          setShowDataLoadError(false)
        }}
        onClose={() => {
          setShowDataLoadError(false)
        }}
      />
    </VStack>
  )
}
